import React, { useMemo, ReactNode } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  useMediaQuery,
  Paper,
  Box,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { DialogActions, DialogTitle } from 'components/WizardWrapper/components';
import {
  Warning as WarningIcon,
  Delete as DeleteIcon,
  CheckCircle as SuccessIcon,
} from '@mui/icons-material';

export interface ConfirmationDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title?: string;
  message?: string | ReactNode;
  additionalInfo?: string;
  icon?: React.ReactElement<any, any> | null;
  confirmButtonText?: string;
  cancelButtonText?: string;
  confirmButtonDisabled?: boolean;
  hideCancelButton?: boolean;
  hideConfirmButton?: boolean;
  dialogWidth?: string | number;
  variant?: 'default' | 'delete' | 'warning' | 'success';
}

type ConfirmButtonColorType =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'error'
  | 'info'
  | 'warning';

export const ConfirmationDialog = React.memo(
  ({
    open,
    onClose,
    onConfirm,
    title,
    message,
    additionalInfo,
    icon,
    confirmButtonText,
    cancelButtonText,
    confirmButtonDisabled = false,
    hideCancelButton = false,
    hideConfirmButton = false,
    variant = 'default',
  }: ConfirmationDialogProps) => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

    const dialogPaperSx = useMemo(
      () => ({
        borderRadius: 2,
        boxShadow: theme.shadows[24],
        overflow: 'hidden',
        background: theme.palette.background.paper,
        width: isMobile ? '100%' : '450px',
        maxWidth: '90vw',
        margin: isMobile ? theme.spacing(2, 0) : 0,
        zIndex: theme.zIndex.modal,
      }),
      [theme, isMobile],
    );

    const defaultTitle = useMemo(() => {
      if (title) return title;
      switch (variant) {
        case 'delete':
          return 'Delete Confirmation';
        case 'warning':
          return 'Warning';
        case 'success':
          return 'Success';
        default:
          return 'Confirm Action';
      }
    }, [variant, title]);

    const defaultMessage = useMemo(() => {
      if (message) return message;
      switch (variant) {
        case 'delete':
          return 'Are you sure you want to delete this item? This action cannot be undone.';
        case 'warning':
          return 'Please be aware of the following warnings before proceeding.';
        case 'success':
          return 'The operation was successful.';
        default:
          return 'Are you sure you want to proceed?';
      }
    }, [variant, message]);

    const defaultConfirmButtonText = useMemo(() => {
      if (confirmButtonText) return confirmButtonText;
      switch (variant) {
        case 'delete':
          return 'Delete';
        case 'warning':
          return 'Proceed';
        case 'success':
          return 'OK';
        default:
          return 'Confirm';
      }
    }, [variant, confirmButtonText]);

    const defaultCancelButtonText = useMemo(() => {
      if (cancelButtonText) return cancelButtonText;
      return 'Cancel';
    }, [cancelButtonText]);

    const defaultIcon = useMemo<React.ReactElement<any, any> | null>(() => {
      if (icon) return icon;
      switch (variant) {
        case 'delete':
          return <DeleteIcon color="error" />;
        case 'warning':
          return <WarningIcon color="warning" />;
        case 'success':
          return <SuccessIcon color="success" />;
        default:
          return null;
      }
    }, [variant, icon]);

    const confirmButtonColor = useMemo<ConfirmButtonColorType>(() => {
      switch (variant) {
        case 'delete':
          return 'error';
        case 'warning':
          return 'warning';
        case 'success':
          return 'success';
        default:
          return 'primary';
      }
    }, [variant]);

    const renderMessage =
      typeof defaultMessage === 'string' ? (
        <Typography
          variant="body1"
          sx={{ mb: additionalInfo ? 1.5 : 0, mt: 2 }}
        >
          {defaultMessage}
        </Typography>
      ) : (
        <Box sx={{ mt: 2, mb: additionalInfo ? 1.5 : 0 }}>{defaultMessage}</Box>
      );

    return (
      <Dialog
        data-testid="confirmation-dialog"
        open={open}
        onClose={onClose}
        PaperComponent={Paper}
        slotProps={{
          paper: {
            sx: dialogPaperSx,
          },
        }}
      >
        <DialogTitle
          data-testid="confirmation-dialog-title"
          title={defaultTitle}
          onClose={onClose}
          icon={defaultIcon}
        />

        <DialogContent data-testid="confirmation-dialog-content">
          {renderMessage}
          {additionalInfo && (
            <Typography
              variant="body2"
              sx={{
                color: 'text.secondary',
              }}
            >
              {additionalInfo}
            </Typography>
          )}
        </DialogContent>

        <DialogActions
          showBackButton={!hideCancelButton}
          showNextButton={!hideConfirmButton}
          nextButtonText={defaultConfirmButtonText}
          backButtonText={defaultCancelButtonText}
          nextButtonDisabled={confirmButtonDisabled}
          nextButtonColor={confirmButtonColor}
          onBack={onClose}
          onNext={onConfirm}
          activeStep={1}
        />
      </Dialog>
    );
  },
);
